import type { ReactNode } from "react";
import React, { createContext, useContext, useState, useCallback } from "react";

interface ProjectThumbHotlinksContextType {
  openInstanceId: string | null;
  openInstance: (instanceId: string) => void;
  closeInstance: (instanceId: string) => void;
  isInstanceOpen: (instanceId: string) => boolean;
}

const ProjectThumbHotlinksContext = createContext<ProjectThumbHotlinksContextType | undefined>(undefined);

interface ProjectThumbHotlinksProviderProps {
  children: ReactNode;
}

export const ProjectThumbHotlinksProvider: React.FC<ProjectThumbHotlinksProviderProps> = ({ children }) => {
  const [openInstanceId, setOpenInstanceId] = useState<string | null>(null);

  const openInstance = useCallback((instanceId: string) => {
    setOpenInstanceId(instanceId);
  }, []);

  const closeInstance = useCallback((instanceId: string) => {
    setOpenInstanceId((current) => (current === instanceId ? null : current));
  }, []);

  const isInstanceOpen = useCallback(
    (instanceId: string) => {
      return openInstanceId === instanceId;
    },
    [openInstanceId]
  );

  const value = {
    openInstanceId,
    openInstance,
    closeInstance,
    isInstanceOpen,
  };

  return <ProjectThumbHotlinksContext.Provider value={value}>{children}</ProjectThumbHotlinksContext.Provider>;
};

export const useProjectThumbHotlinks = (): ProjectThumbHotlinksContextType => {
  const context = useContext(ProjectThumbHotlinksContext);
  if (context === undefined) {
    throw new Error("useProjectThumbHotlinks must be used within a ProjectThumbHotlinksProvider");
  }
  return context;
};
